






















import mixins from 'vue-typed-mixins'
import { mapActions } from 'vuex'
import { ADD_TOAST_MESSAGE } from 'vuex-toast'
import AbstractAuthForm from '@/abstract/AuthForm'
import PasswordForm from '@/abstract/PasswordForm'
import AuthLayout from '@/layout/AuthLayout.vue'
import EmailButton from '@/components/EmailButton/EmailButton.vue'
import { RouteNames } from '@/router'
import store from '@/store'
import { AUTH__FORGOT, AUTH__RECOVER, AUTH__VERIFY_CODE } from '@/store/actions'

import type { Route } from 'vue-router'
import type { AxiosPromise } from 'axios'
import type { AuthRecoveryPayload } from '@/store/modules/auth'

interface Data {
  isSuccess: boolean
  isInProgress: boolean
}

interface Methods {
  [AUTH__FORGOT]: () => AxiosPromise<void>
  [AUTH__RECOVER]: (payload: AuthRecoveryPayload) => AxiosPromise<boolean>
  handleSubmit: () => Promise<void>
  handleForgot: () => Promise<void>
  handleRecover: () => Promise<void>
}

interface Computed {
  code?: string
  title: string
  text: string
  buttonText: string
  isFormValid: boolean
}

async function verifyCode(
  to: Route,
  from: Route,
  next: (to?: string) => void,
) {
  if (to.params?.code) {
    try {
      const isCodeValid = await store.dispatch(AUTH__VERIFY_CODE, to.params.code)

      if (!isCodeValid) {
        return next(RouteNames.InvalidLink)
      }
    } catch (e) {
      return next(RouteNames.InvalidLink)
    }
  }

  next()
}

export default mixins(AbstractAuthForm, PasswordForm).extend<
  Data,
  Methods,
  Computed
>({
  beforeRouteEnter: verifyCode,
  beforeRouteUpdate: verifyCode,
  components: {
    AuthLayout,
    EmailButton,
  },
  data() {
    return {
      isSuccess: false,
      isInProgress: false,
    }
  },
  computed: {
    code() {
      return this.$route.params.code
    },
    title(): string {
      if (this.isSuccess) {
        return this.$tc('auth.resetPasswordProcessTitle')
      }

      if (this.code) {
        return this.$tc('auth.setNewPassword')
      }

      return this.$tc('auth.recoveryTitle')
    },
    text(): string {
      if (this.isSuccess) {
        return this.$tc('auth.resetPasswordProcessText', 1, {
          email: this.authEmail,
        })
      }

      if (this.code) {
        return this.$tc('auth.passwordConditions')
      }

      return this.$tc('auth.setEmailForRecover')
    },
    buttonText(): string {
      return this.$tc(`auth.${this.code ? 'setPassword' : 'callRecovery'}`)
    },
    isFormValid(): boolean {
      if (this.code) {
        return (
          this.isPasswordHasCapital &&
          this.isPasswordHasRegular &&
          this.isPasswordHasNumbers
        )
      }
      return !!this.authEmail
    },
  },
  methods: {
    ...mapActions([AUTH__FORGOT, AUTH__RECOVER]),
    async handleSubmit() {
      if (this.isInProgress) {
        return
      }

      try {
        this.isInProgress = true
        const task = this.code ? this.handleRecover() : this.handleForgot()
        await task
        this.isInProgress = false
      } catch (e) {
        this.isInProgress = false
      }
    },
    async handleForgot() {
      await this[AUTH__FORGOT]()

      this.isSuccess = true
    },
    async handleRecover() {
      if (!this.code) {
        return
      }

      await this[AUTH__RECOVER]({
        key: this.code,
        password: this.password,
      })

      this.$store.dispatch(ADD_TOAST_MESSAGE, {
        text: this.$tc('auth.passwordComplete'),
        type: 'success',
      })

      this.$router.push('/login')
    },
  },
  metaInfo() {
    return { title: this.$tc('auth.recoveryTitle') }
  },
})
